import React, {useState, FC, useMemo} from 'react';
import dynamic from 'next/dynamic';
import { styled } from '@mui/material'
import { Box } from '@mui/material';
import OutlinedInput from "@mui/material/OutlinedInput";
import {chains} from "@/configs";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import {
    AccountMeta, Connection,
    PublicKey, RpcResponseAndContext, SignatureResult,
    SystemProgram,
    SYSVAR_RENT_PUBKEY, Transaction,
    TransactionInstruction
} from "@solana/web3.js";
import { u8 } from '@solana/buffer-layout';
import { struct } from '@solana/buffer-layout';
import {enqueueSnackbar} from "notistack";
import {useRequest} from "ahooks";
import { observer } from 'mobx-react-lite';
import { useStoreOrNull } from '@/stores/StoreProvider';
import WalletSubHeader from '@/components/WalletSubHeader';

const solanaChain = chains.find(chain => chain.nameId === 'solana')!;

const Root = styled(Box)({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginTop: 80,
    maxWidth: 600,
    marginRight: 'auto',
    marginLeft: 'auto',
});

const Description = styled(Typography)(({theme}) => ({
    marginBottom: 20,
    color: theme.palette.common.N250,
    textAlign: 'center',
    '& > a': {
        color: theme.palette.common.N250,
        textDecoration: 'underline',
    }
}));

const Container = styled(Box)({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
    maxWidth: 400,
    paddingRight: 20,
    paddingLeft: 20,
});

async function solanaConfirmTransactionWithRetry(
    connection: Connection,
    signature: string,
    serializedTrx: Uint8Array,
    commitment: 'confirmed' | 'processed' = 'confirmed',
): Promise<RpcResponseAndContext<SignatureResult>> {
    let interval;
    try {
        interval = setInterval(() => {
            connection.sendRawTransaction(serializedTrx, { skipPreflight: true });
        }, 1000);
        const latestBlockhash = await connection.getLatestBlockhash();
        const result = await connection.confirmTransaction(
            {
                signature,
                blockhash: latestBlockhash.blockhash,
                lastValidBlockHeight: latestBlockhash.lastValidBlockHeight,
            },
            commitment,
        );
        clearInterval(interval);
        return result;
    } finally {
        clearInterval(interval);
    }
}

const SetFeeLayout = struct<any>([
    u8('instruction'),
    u8('feeConfigNonce'),
    u8('feeRate'),
]);
function createSetFeeInstruction(feeRate: number, referrer: PublicKey): TransactionInstruction {
    const mayanProgram = new PublicKey(solanaChain.mayanContractAddress);
    const [feeConfig, feeConfigNonce] = PublicKey.findProgramAddressSync(
        [Buffer.from('REFERRERFEE'), referrer.toBuffer()],
        mayanProgram,
    );
    console.log(feeConfig.toBase58());
    const keys: Array<AccountMeta> = [
        {pubkey: referrer, isWritable: false, isSigner: true},
        {pubkey: feeConfig, isWritable: true, isSigner: false},
        {pubkey: referrer, isWritable: true, isSigner: true},
        {pubkey: SYSVAR_RENT_PUBKEY, isWritable: false, isSigner: false},
        {pubkey: SystemProgram.programId, isWritable: false, isSigner: false},
    ];
    const data = Buffer.alloc(SetFeeLayout.span);
    const fields = {
        instruction: 131,
        feeConfigNonce,
        feeRate,
    }
    SetFeeLayout.encode(fields, data);
    return new TransactionInstruction({
        keys,
        programId: mayanProgram,
        data,
    });
}
const ReferrerFee: FC = observer(() => {
    const [fee, setFee] = useState<number | string>('');
    const [loading, setLoading] = useState<boolean>(false);
    const store = useStoreOrNull();
    if (!store) return null;

    // Find the connected Solana wallet
    const solanaWallet = store.wallets.connectedWallets.find(w => w.type === 'solana');
    const connected = Boolean(solanaWallet?.isReady);
    const publicKey = solanaWallet && 'publicKey' in solanaWallet ? (solanaWallet as any).publicKey as PublicKey | null : null;

    // Get Solana connection from the SVM connector
    const connection = store.wallets.svm.connections.get(0) ?? null;

    const { data: currentRate } = useRequest(async () => {
        if (!publicKey || !connection) {
            return null;
        }
        const mayanProgram = new PublicKey(solanaChain.mayanContractAddress);
        const [referrerFee] = PublicKey.findProgramAddressSync(
            [Buffer.from('REFERRERFEE'), publicKey.toBuffer()],
            mayanProgram,
        );
        const info = await connection.getAccountInfo(referrerFee);
        if (!info) {
            return null;
        }
        return info.data[0];
    }, {
        pollingInterval: 3000,
        refreshDeps: [publicKey, connection],
    })
    const handleConnect = async () => {
        if (solanaChain) {
            store.chains.setOriginChain(solanaChain);
            store.modal.open('origin');
        }
    }

    const handleClick = async () => {
        try {
            if (!connected) {
                handleConnect();
                return;
            }
            if (!publicKey || !connection || !solanaWallet) {
                return;
            }
            setLoading(true);
            const instruction = createSetFeeInstruction(Number(fee), publicKey);
            console.log('getting blockhash');
            const { blockhash } = await connection.getLatestBlockhash();
            const trx = new Transaction({
                feePayer: publicKey,
                recentBlockhash: blockhash,
            });
            trx.add(instruction);
            console.log('signing transaction');
            const signedTrx = await (solanaWallet as any).signTransaction(trx);
            const signature = await connection.sendRawTransaction(signedTrx.serialize(),  { skipPreflight: true });
            enqueueSnackbar('Transaction sent', { variant: 'info' });
            await solanaConfirmTransactionWithRetry(connection, signature, signedTrx.serialize());
            enqueueSnackbar('Transaction confirmed', { variant: 'success' });
            setLoading(false);
        } catch (err) {
            enqueueSnackbar('Transaction failed', { variant: 'error' });
            setLoading(false);
        }
    }
    const buttonText = useMemo<string>(() => {
        if (!connected) {
            return 'Connect Referrer Wallet';
        }
        if (!fee && fee !== 0) {
            return 'Enter Fee Rate';
        }
        if (Number(fee) < 0 || Number(fee) > 50) {
            return 'Invalid Fee Rate';
        }
        return 'Execute Transaction';
    }, [connected, fee]);

    return (
        <Root>
            <WalletSubHeader vms={['solana']} />
            <Description variant="appSmallRegular">
                As a referrer, you have the flexibility to set your referral fee according to your preferences. The referral fee can range from 0 <a target="_blank" href="https://en.wikipedia.org/wiki/Basis_point" >basis points (bps)</a> up to 50 bps.
                Note that Mayan also charges at least the same fee rate.
                <br />
                <i>Example: if you set your referrer fee to 10 bps, Mayan would also receive 10 bps on swaps. As a result the total fee paid by the user would amount to 20 bps.</i>
            </Description>
            <Container>
                <OutlinedInput
                    fullWidth
                    size="large"
                    placeholder="[0, 50] bps"
                    type="number"
                    value={fee}
                    onChange={(e) => setFee(Number(e.target.value))}

                />
                {(currentRate || currentRate === 0) && (
                    <Typography sx={{ alignSelf: 'flex-start', mt: 0.75 }} align="right" color="common.N250" variant="appTinyRegular">Current Rate: {currentRate} bps</Typography>
                )}
                <Button
                    variant="contained"
                    size="large"
                    fullWidth
                    sx={{
                        mt: 3,
                    }}
                    onClick={handleClick}
                    disabled={loading || (connected && ((!fee && Number(fee) !== 0) || (Number(fee) < 0 || Number(fee) > 50)))}
                >
                    {buttonText}
                </Button>
            </Container>

        </Root>
    )

});

export default dynamic(() => Promise.resolve(ReferrerFee), { ssr: false });
